import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, BookOpen, Baby, FileText, Sparkles } from "lucide-react";
import { BuyButton } from "./BuyButton";

const EASE = [0.22, 1, 0.36, 1];

export const Hero = ({ content }) => {
  const wrapRef = useRef(null);
  const sectionRef = useRef(null);
  const [tilt, setTilt] = useState("rotateY(-8deg) rotateX(4deg)");

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const coverY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const blobA = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const blobB = useTransform(scrollYProgress, [0, 1], [0, 140]);

  const onMove = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt(`rotateY(${x * 18}deg) rotateX(${-y * 14}deg)`);
  };

  const stars = Math.round(content.rating || 5);
  const words = (content.title || "").split(" ");

  return (
    <section id="libro" ref={sectionRef} className="paper-texture relative overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: blobA }} className="pointer-events-none absolute -left-24 top-16 h-72 w-72 rounded-full bg-terracotta-light blur-2xl" aria-hidden />
      <motion.div style={{ y: blobB }} className="pointer-events-none absolute -right-20 bottom-10 h-80 w-80 rounded-full bg-olive-light blur-2xl" aria-hidden />

      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-2 lg:pb-28 lg:pt-24">
        <div className="space-y-7">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-terracotta/30 bg-terracotta-light px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-terracotta-dark"
            data-testid="hero-age-badge"
          >
            <Baby className="h-3.5 w-3.5" /> Quaderno operativo · {content.age_range}
          </motion.span>

          <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-olive sm:text-5xl lg:text-6xl" data-testid="hero-title" aria-label={content.title}>
            {words.map((word, i) => (
              <span key={i} className="-mb-2 inline-block overflow-hidden pb-2 align-bottom" aria-hidden>
                <motion.span
                  className="inline-block will-change-transform"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.08, ease: EASE }}
                >
                  {word}
                  {i < words.length - 1 ? " " : ""}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="font-editorial text-xl italic text-terracotta sm:text-2xl"
            data-testid="hero-subtitle"
          >
            {content.subtitle}
          </motion.p>

          {content.description?.[0] && (
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
              className="max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg"
              data-testid="hero-description"
            >
              {content.description[0]}
            </motion.p>
          )}

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.82, ease: EASE }}
            className="flex items-center gap-2"
            data-testid="hero-rating"
          >
            <span className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`h-5 w-5 ${i < stars ? "fill-amazon-orange text-amazon-orange" : "text-stone-300"}`} />
              ))}
            </span>
            <span className="text-sm font-semibold text-stone-700">{Number(content.rating).toFixed(1)} su Amazon</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95, ease: EASE }}
            className="flex flex-wrap items-center gap-4 pt-1"
          >
            <BuyButton url={content.amazon_url} price={content.price} testid="hero-buy-amazon-button" />
            <motion.a
              href="#estratto"
              data-testid="hero-excerpt-link"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 rounded-full border-2 border-olive px-6 py-3 font-display text-base font-semibold text-olive transition-colors hover:bg-olive hover:text-white sm:text-lg"
            >
              <BookOpen className="h-5 w-5" /> Sfoglia l'estratto gratuito
            </motion.a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="flex flex-wrap gap-x-6 gap-y-2 pt-2 text-sm text-stone-500"
          >
            <span className="inline-flex items-center gap-1.5" data-testid="hero-pages-info">
              <FileText className="h-4 w-4 text-terracotta" /> {content.pages_count} pagine
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="hero-activities-info">
              <Sparkles className="h-4 w-4 text-terracotta" /> 20 attività creative
            </span>
            <span className="inline-flex items-center gap-1.5">Spedizione rapida con Amazon Prime</span>
          </motion.div>
        </div>

        <motion.div style={{ y: coverY }} className="relative flex justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: -4 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 0.35, ease: EASE }}
            className="relative flex justify-center"
          >
            <div className="absolute -top-6 -left-2 h-24 w-24 animate-float-slow rounded-full bg-terracotta-light" aria-hidden />
            <div className="absolute -bottom-8 right-0 h-32 w-32 animate-float-slow rounded-full bg-olive-light" style={{ animationDelay: "1.5s" }} aria-hidden />

            <div ref={wrapRef} onMouseMove={onMove} onMouseLeave={() => setTilt("rotateY(-8deg) rotateX(4deg)")} className="relative [perspective:1200px]">
              <div
                className="book-shadow relative overflow-hidden rounded-r-2xl rounded-l-md border border-stone-200 bg-white transition-transform duration-200 ease-out"
                style={{ transform: tilt, transformStyle: "preserve-3d" }}
                data-testid="hero-book-cover"
              >
                <img src={content.cover_url} alt={`Copertina di ${content.title}`} className="h-auto w-[300px] object-cover sm:w-[360px]" />
                <div className="pointer-events-none absolute inset-y-0 left-0 w-3 bg-gradient-to-r from-black/25 to-transparent" aria-hidden />
              </div>
              <motion.span
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 1.15, ease: EASE }}
                className="absolute -right-4 -top-4 rotate-6 rounded-full bg-olive px-4 py-2 font-display text-sm font-bold text-white shadow-lg"
                data-testid="hero-author-badge"
              >
                di {content.author}
              </motion.span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
